import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './asset-pms.style.js';
import { getPreventiveMaintenanceByAsset, getUsersByCompany } from '@cmms/api';
import { db } from '../../../../services/auth.service.js';



@customElement('asset-pms')
export class AssetPMs extends LitElement {
  @property({ type: Object }) asset: any = null;
  @property({ type: String }) companyId: string = '';

  @state() private pmTemplates: Array<any> = [];
  @state() private users: Array<any> = [];
  @state() private isLoading = false;
  @state() private error: string | null = null;

  static styles = styles;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadUsers();
    await this.loadPMs();
  }

  async updated(changed: Map<string, unknown>) {
    if (changed.has('asset') && this.asset) {
      await this.loadPMs();
    }
  }

  private async loadUsers() {
    if (!this.companyId) return;
    
    try {
      const result = await getUsersByCompany(db, this.companyId);
      this.users = Array.isArray(result) ? result : (result?.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      this.users = [];
    }
  }

  private async loadPMs() {
    if (!this.asset) return;

    const ids = Array.from(new Set([
      this.asset?.id,
      this.asset?.asset_id,
      this.asset?.model?.asset_id
    ].filter(Boolean).map((v: any) => String(v))));

    if (ids.length === 0) {
      this.pmTemplates = [];
      return;
    }

    try {
      this.isLoading = true;
      this.error = null;
      const collected: any[] = [];

      for (const id of ids) {
        const resp: any = await getPreventiveMaintenanceByAsset(db, id, this.companyId);
        const raw = Array.isArray(resp) ? resp : (resp?.data || []);
        if (raw && raw.length) collected.push(...raw);
      }

      // Ensure each has {id, model}
      this.pmTemplates = collected.map((p: any) => {
        if (p.model && typeof p.model === 'object' && p.model.constructor && p.model.constructor.name === 'PreventiveMaintenanceModel') {
          return { id: p.id || p.$id, model: p.model };
        } else {
          // If model creation failed, create a proper model structure from raw data
          const rawData = p.model || p;
          return { 
            id: p.id || p.$id, 
            model: {
              id: rawData.id || p.id || p.$id,
              name: rawData.name || 'No name',
              description: rawData.description || 'No description',
              freq_unit: rawData.freq_unit || 'months',
              freq_value: rawData.freq_value || 1,
              anchor_date: rawData.anchor_date || Date.now(),
              grace_days: rawData.grace_days || 0,
              next_due_at: rawData.next_due_at || Date.now(),
              active: rawData.active !== undefined ? rawData.active : true,
              company_id: rawData.company_id || '',
              asset_id: rawData.asset_id || '',
              default_priority_cat: rawData.default_priority_cat || 2,
              default_assigned_to: rawData.default_assigned_to || '',
              checklist: rawData.checklist || [],
              parts: rawData.parts || [],
              created_by: rawData.created_by || '',
              updated_by: rawData.updated_by || '',
              created_at: rawData.created_at || Date.now(),
              updated_at: rawData.updated_at || Date.now()
            }
          };
        }
      });
    } catch (e: any) {
      this.error = e?.message || 'Failed to load PMs';
      this.pmTemplates = [];
    } finally {
      this.isLoading = false;
    }
  }

  private formatDate(ts?: number | null) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString();
  }

  private getUserName(userId?: string) {
    if (!userId) return 'Unassigned';
    const user = this.users.find(u => u.id === userId || u.$id === userId || u.user_id === userId || u.uid === userId);
    return user?.model?.name || user?.model?.display_name || user?.name || user?.display_name || user?.email || `User ${userId}`;
  }

  private openPM(pm: any) {
    const viewer = document.createElement('pm-viewer') as any;
    viewer.open = true;
    viewer.pmTemplate = pm;
    viewer.companyId = this.companyId;
    document.body.appendChild(viewer);
    viewer.addEventListener('modal-closed', () => {
      document.body.removeChild(viewer);
      this.loadPMs(); // Reload PMs after closing viewer
    }, { once: true });
  }

  private handleAddPM() {
    const modal = document.createElement('pm-modal') as any;
    modal.open = true;
    modal.companyId = this.companyId;
    modal.firestore = db;
    
    // Pre-fill the asset_id in the formData
    const assetId = this.asset?.id || this.asset?.asset_id;
    modal.formData = {
      name: '',
      description: '',
      asset_id: assetId,
      freq_unit: 'months',
      freq_value: 1,
      anchor_date: Date.now(),
      grace_days: 0,
      default_assigned_to: '',
      default_priority_cat: 2,
      is_active: true
    };
    
    document.body.appendChild(modal);
    modal.addEventListener('modal-closed', () => {
      document.body.removeChild(modal);
      this.loadPMs(); // Reload PMs after adding new one
    }, { once: true });
  }

  render() {
    return html`
      <div class="container">
        <div class="header-section">
          <div class="info-description">
            Preventive Maintenance schedules for this asset
          </div>
          <sl-button variant="primary" size="small" @click=${this.handleAddPM}>
            <sl-icon slot="prefix" name="plus-circle"></sl-icon>
            Add PM
          </sl-button>
        </div>

        ${this.isLoading ? html`
          <div style="display:flex;justify-content:center;padding:1rem">
            <sl-spinner></sl-spinner>
          </div>
        ` : this.error ? html`
          <sl-alert variant="danger" open>${this.error}</sl-alert>
        ` : this.pmTemplates.length === 0 ? html`
          <div class="empty-state">
            No PM templates for this asset
          </div>
        ` : html`
          <div class="data-table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Frequency</th>
                  <th>Next Due</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${this.pmTemplates.map((pm: any) => html`
                  <tr class="clickable-row" @click=${() => this.openPM(pm)}>
                    <td>${pm.model?.name || 'Unnamed PM'}</td>
                    <td>${pm.model?.description || 'No description'}</td>
                    <td>${pm.model?.freq_value || 1} ${pm.model?.freq_unit || 'months'}</td>
                    <td>${this.formatDate(pm.model?.next_due_at)}</td>
                    <td>${this.getUserName(pm.model?.default_assigned_to)}</td>
                    <td>
                      <span class="status-badge ${pm.model?.active ? 'active' : 'inactive'}">
                        ${pm.model?.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asset-pms': AssetPMs;
  }
}

